import { FaqList } from "@/components/home/FaqList";
import { JsonLd } from "@/components/JsonLd";
import { LOCATIONS } from "@/content/locations";
import { Link } from "@tanstack/react-router";
import { POSTS } from "@/content/posts";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/content/site";
import { Section } from "@/components/Section";
import { faqSchema } from "@/lib/schema";
import { HOME_FAQS } from "@/components/home/home-content";
import { ArrowUpRight } from "lucide-react";

export function HomeResources() {
  return (
    <>
      {/* FAQ */}
      <Section eyebrow="FAQ" title={<>Quick <span className="text-gradient">answers.</span></>}>
        <FaqList />
        <JsonLd data={faqSchema(HOME_FAQS)} />
      </Section>

      {/* GEO-TARGETED SEO SNIPPETS */}
      <Section
        eyebrow="Where I rank clients"
        title={<>Geo-targeted SEO across <span className="text-gradient">Bangladesh & beyond.</span></>}
        subtitle="Short, city-specific snippets — the kind Google and AI engines lift into local packs and Overviews. Click any city for the full local SEO playbook."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LOCATIONS.map((l, i) => {
            const snippet = l.intro.split(". ").slice(0, 2).join(". ").replace(/\.?$/, ".");
            return (
              <Reveal key={l.slug} delay={(i % 3) * 0.06}>
                <Link
                  to="/seo-services/$location"
                  params={{ location: l.slug }}
                  className="group block h-full rounded-3xl border border-border p-7 bg-card hover:border-primary transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-primary">{l.region}</div>
                      <h3 className="mt-2 font-display text-xl">SEO Expert in {l.city}</h3>
                    </div>
                    <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary shrink-0" />
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-4">{snippet}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {l.industries.slice(0, 3).map((ind) => (
                      <span key={ind} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground">{ind}</span>
                    ))}
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-8 text-sm text-muted-foreground">
          Looking for a city not listed?{" "}
          <Link to="/seo-locations" className="text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary">
            See all SEO locations →
          </Link>
        </div>
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Geo-targeted SEO services by Freelancer Tamal",
          itemListElement: LOCATIONS.map((l, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            url: `${SITE.url}/seo-services/${l.slug}`,
            name: `SEO Expert in ${l.city}, ${l.country}`,
          })),
        }} />
      </Section>

      {/* BLOG TEASER */}
      <Section
        eyebrow="Field notes"
        title={<>Plain-English SEO, <span className="text-gradient">no fluff.</span></>}
      >
        <div className="grid md:grid-cols-3 gap-6">
          {POSTS.slice(0, 3).map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <Link
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="group block h-full rounded-3xl border border-border p-8 hover:bg-card transition"
              >
                <div className="text-xs uppercase tracking-[0.18em] text-primary">{p.category}</div>
                <h3 className="mt-4 font-display text-2xl leading-snug">{p.title}</h3>
                <p className="mt-4 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                <div className="mt-8 text-xs text-muted-foreground">{p.readMin} min read</div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="!pt-0">
        <div className="relative overflow-hidden rounded-[2rem] p-12 lg:p-24 text-center bg-card border border-border">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 animate-glow-pulse"
            style={{ background: "var(--gradient-radial)" }}
          />
          <h2 className="font-display text-5xl md:text-7xl leading-[1] max-w-4xl mx-auto">
            Ready to make Google <em className="text-gradient not-italic">your top channel?</em>
          </h2>
          <p className="mt-8 max-w-xl mx-auto text-muted-foreground">
            Tell me about your project. I reply within one business day with
            an honest take on whether I can help.
          </p>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition"
          >
            Start the conversation
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>
    </>
  );
}
